import type { UseQueryOptions } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AuditLogResponse,
  TaskFlowHistoryOnDate,
  TaskFlowHistoryTask,
  TaskFlowHistoryType,
} from '../../../api/sprints-client/sprints-client.type';
import type { MinifiedTask } from '../../../api/tasks-client/task-client.type';
import { isCustomMeasure } from '../../../api/work-periods-client/work-periods-client.helpers';
import {
  useAvailableMeasures,
  useWorkPeriodsMeasureSingular,
  useWorkPeriodsTasksSingular,
} from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  type FlowByPaceDataOverTime,
  type FlowByPhaseDataOverTime,
  type HealthScoreData,
  Measure,
  ModuleType,
  Transformer,
  type UseWorkPeriodsMeasurePayloadSingular,
} from '../../../api/work-periods-client/work-periods-client.type';
import type { PhaseLabel } from '../../../helpers/phase-labels/phase-labels.type';
import { getLastNotNullValue } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import type { KeyProcessMeasuresData } from '../../../helpers/process-analysis-shared/process-analysis-shared.type';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { filterPaceData, getScopeMetadata, transformForPaceChart } from '../work-pace/pace-section.helpers';
import type { FlowData } from '../work-periods.type';
import { Metric, WorkPeriodType } from '../work-periods.type';

/**
 * Custom hook to fetch the payload for the work periods measures
 *
 * @param {Measure[]} measures - The measures to fetch
 * @returns {UseWorkPeriodsMeasurePayloadSingular} - The payload for the work periods measures
 */
const usePayload = (measures: Measure[]): UseWorkPeriodsMeasurePayloadSingular => {
  const { selectedBoardId, selectedWorkPeriods, dateRange, selectedWorkPeriodType } = useWorkPeriodStore();

  // Pull measure metadata for this scope so custom (DSL) measures are detected by their
  // is_custom flag rather than a hardcoded list. Readiness / Complexity remain custom via the
  // helper's one-off allowlist. Custom measures must route to the custom endpoints.
  const workPeriodId = selectedWorkPeriods.length > 0 ? selectedWorkPeriods[0].id : '';
  const { data: availableMeasuresResponse } = useAvailableMeasures({
    payload: { scope: [ModuleType.WorkPeriod], work_period_id: workPeriodId },
    options: { enabled: !!selectedBoardId && !!workPeriodId },
  });
  const availableMeasures = availableMeasuresResponse?.work_period?.measures || [];

  const isCustom = measures.length === 1 && isCustomMeasure(measures[0], availableMeasures);

  const defaultPayload = {
    subproject_id: selectedBoardId || '',
    measure_name: measures,
    is_custom: isCustom,
    module_type: ModuleType.WorkPeriod,
  };

  const customPayload =
    selectedWorkPeriodType === WorkPeriodType.Defined && selectedWorkPeriods.length > 0
      ? {
          work_period_id: selectedWorkPeriods[0].id,
          start_date: dayjs(selectedWorkPeriods[0].start_date).format('YYYY-MM-DD'),
          end_date: dayjs(selectedWorkPeriods[0].end_date).format('YYYY-MM-DD'),
        }
      : {
          start_date: dateRange.startDate ? dayjs(dateRange.startDate).format('YYYY-MM-DD') : '',
          end_date: dateRange.endDate ? dayjs(dateRange.endDate).format('YYYY-MM-DD') : '',
        };

  return {
    ...defaultPayload,
    ...customPayload,
  } as UseWorkPeriodsMeasurePayloadSingular;
};

/**
 * Custom hook to fetch the flow by phase data along with the optional
 * `phases` companion array carrying per-workflow display-label overrides.
 *
 * @returns {object} An object containing the fetched data, the optional
 *   per-workflow `phases` overrides, an `isFetching` flag (true on initial
 *   load and background refetches) and an `isLoading` flag (true only while
 *   no cached data is available yet — used to drive skeleton placeholders
 *   without flashing on cached navigations).
 */
const useFlowByPhaseData = (): {
  data: FlowByPhaseDataOverTime | undefined;
  phases: PhaseLabel[] | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const { selectedBoardId, useGroups } = useWorkPeriodStore();

  const flowByPhasePayload = usePayload([Measure.FlowByPhase]);
  const { data: flowByPhaseData, query: flowByPhaseQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...flowByPhasePayload,
      use_groups: useGroups,
    },
    options: {
      enabled: !!selectedBoardId,
    },
  });

  const data = flowByPhaseData?.flow_by_phase;
  const phases = flowByPhaseData?.phases;
  const isFetching = flowByPhaseQuery.isFetching;
  const isLoading = flowByPhaseQuery.isLoading;

  return { data, phases, isFetching, isLoading };
};

/**
 * Custom hook to fetch the flow by pace data
 *
 * @returns {object} An object containing the fetched data, an `isFetching`
 *   flag (true on initial load and background refetches) and an `isLoading`
 *   flag (true only while no cached data is available yet)
 */
const useFlowByPaceData = (): {
  data: FlowByPaceDataOverTime | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const { selectedBoardId } = useWorkPeriodStore();

  const flowByPacePayload = usePayload([Measure.FlowByPace]);
  const { data: flowByPaceData, query: flowByPaceQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...flowByPacePayload,
    },
    options: {
      enabled: !!selectedBoardId,
    },
  });

  const data = flowByPaceData?.flow_by_pace;
  const isFetching = flowByPaceQuery.isFetching;
  const isLoading = flowByPaceQuery.isLoading;

  return { data, isFetching, isLoading };
};

/**
 * Custom hook to fetch the burns data
 *
 * @returns {object} An object containing the fetched data, an `isFetching`
 *   flag (true on initial load and background refetches) and an `isLoading`
 *   flag (true only while no cached data is available yet)
 */
const useBurnsData = (): { data: FlowData | undefined; isFetching: boolean; isLoading: boolean } => {
  const { selectedBoardId } = useWorkPeriodStore();

  const burnsPayload = usePayload([Measure.Burns, Measure.HistoricalBurns]);
  const { data: burnsData, query: burnsQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...burnsPayload,
    },
    options: {
      enabled: !!selectedBoardId,
    },
  });

  const isFetching = burnsQuery.isFetching;
  const isLoading = burnsQuery.isLoading;
  return { data: burnsData, isFetching, isLoading };
};

/**
 * Custom hook to fetch the key process measures data
 *
 * @returns {object} An object containing the merged measure data, an
 *   `isFetching` flag (true on any initial load or background refetch across
 *   the 8 underlying measure queries) and an `isLoading` flag (true only when
 *   none of the measures has cached data yet — used to drive skeleton tiles
 *   on first paint)
 */
const useKeyProcessMeasuresData = (): {
  data: KeyProcessMeasuresData;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const { selectedBoardId } = useWorkPeriodStore();

  const options = { enabled: !!selectedBoardId };

  const strategyPayload = usePayload([Measure.Strategy]);
  const { data: strategyData, query: strategyQuery } = useWorkPeriodsMeasureSingular<Partial<KeyProcessMeasuresData>>({
    payload: {
      ...strategyPayload,
      transformer: Transformer.LastValueIndexed,
    },
    options,
  });

  const independencePayload = usePayload([Measure.Independence]);
  const { data: independenceData, query: independenceQuery } = useWorkPeriodsMeasureSingular<
    Partial<KeyProcessMeasuresData>
  >({
    payload: {
      ...independencePayload,
      transformer: Transformer.LastValueIndexed,
    },
    options,
  });

  const qualityPayload = usePayload([Measure.Quality]);
  const { data: qualityData, query: qualityQuery } = useWorkPeriodsMeasureSingular<Partial<KeyProcessMeasuresData>>({
    payload: {
      ...qualityPayload,
      transformer: Transformer.TimeSeriesMostRecentNotNull,
    },
    options,
  });

  const cumulativeTransformerPayload = usePayload([Measure.ScopeCreep]);
  const { data: cumulativeTransformerData, query: cumulativeTransformerQuery } = useWorkPeriodsMeasureSingular<
    Partial<KeyProcessMeasuresData>
  >({
    payload: {
      ...cumulativeTransformerPayload,
      transformer: Transformer.CumulativeSingularValue,
    },
    options,
  });

  const readinessPayload = usePayload([Measure.Readiness]);
  const { data: readinessData, query: readinessQuery } = useWorkPeriodsMeasureSingular<
    Record<string, Record<string, number>>
  >({
    payload: {
      ...readinessPayload,
      is_custom: true,
      module_type: ModuleType.WorkPeriod,
      transformer: Transformer.ReturnFirstMetricOnly,
    },
    options,
  });
  const lastReadinessValueRaw = readinessData?.readiness as Record<string, number>;
  let lastReadinessValue = null;
  if (lastReadinessValueRaw) {
    lastReadinessValue = getLastNotNullValue(lastReadinessValueRaw);
  }

  const complexityPayload = usePayload([Measure.Complexity]);
  const { data: complexityData, query: complexityQuery } = useWorkPeriodsMeasureSingular<
    Record<string, Record<string, number>>
  >({
    payload: {
      ...complexityPayload,
      is_custom: true,
      module_type: ModuleType.WorkPeriod,
      transformer: Transformer.SingleLastNonNull,
    },
    options,
  });

  const initialCommittedTasksPayload = usePayload([Measure.InitialCommittedTasks]);
  const { data: initialCommittedTasksData, query: initialCommittedTasksQuery } = useWorkPeriodsMeasureSingular<
    Partial<KeyProcessMeasuresData>
  >({
    payload: {
      ...initialCommittedTasksPayload,
      is_custom: true,
      module_type: ModuleType.WorkPeriod,
      transformer: Transformer.SingleLastNonNull,
    },
    options,
  });

  const initialCommittedPointsPayload = usePayload([Measure.InitialCommittedPoints]);
  const { data: initialCommittedPointsData, query: initialCommittedPointsQuery } = useWorkPeriodsMeasureSingular<
    Partial<KeyProcessMeasuresData>
  >({
    payload: {
      ...initialCommittedPointsPayload,
      is_custom: true,
      module_type: ModuleType.WorkPeriod,
      transformer: Transformer.SingleLastNonNull,
    },
    options,
  });

  const isFetching =
    strategyQuery.isFetching ||
    independenceQuery.isFetching ||
    qualityQuery.isFetching ||
    cumulativeTransformerQuery.isFetching ||
    readinessQuery.isFetching ||
    complexityQuery.isFetching ||
    initialCommittedTasksQuery.isFetching ||
    initialCommittedPointsQuery.isFetching;
  const isLoading =
    strategyQuery.isLoading ||
    independenceQuery.isLoading ||
    qualityQuery.isLoading ||
    cumulativeTransformerQuery.isLoading ||
    readinessQuery.isLoading ||
    complexityQuery.isLoading ||
    initialCommittedTasksQuery.isLoading ||
    initialCommittedPointsQuery.isLoading;
  const data = {
    ...(strategyData ?? {}),
    ...(independenceData ?? {}),
    ...(qualityData ?? {}),
    ...(cumulativeTransformerData ?? {}),
    ...{ [Measure.Readiness]: lastReadinessValue },
    ...(complexityData ?? {}),
    ...(initialCommittedTasksData ?? {}),
    ...(initialCommittedPointsData ?? {}),
  } as KeyProcessMeasuresData;

  return { data, isFetching, isLoading };
};

/**
 * Custom hook to fetch the assessment view data.
 *
 * Exposes per-region loading flags so the consumer can render granular
 * skeletons (health score, key measures) instead of a single full-screen
 * spinner gated on the slowest underlying query.
 *
 * Responsibilities:
 * 1. Fetch the health score measure for the selected work period
 * 2. Fetch the 6 key process measures via `useKeyProcessMeasuresData`
 * 3. Derive `calculatedScopeChange` from the flow-by-pace data
 * 4. Return per-region `isLoading` flags alongside the merged data
 *
 * @returns {object} The merged assessment data plus three first-paint loading
 *   flags: `isLoading` (true while any of the underlying regions has no
 *   cached data yet), `isLoadingScore` (the health-score query specifically),
 *   and `isLoadingKeyMeasures` (the 6-measure aggregate folded with the
 *   flow-by-pace query that feeds the scope_creep tile). All three are false
 *   during background refetches so cached navigations render instantly with
 *   no spinner flash.
 */
const useAssessmentViewData = (): {
  scoreData: HealthScoreData | undefined;
  keyMeasuresData: KeyProcessMeasuresData | undefined;
  calculatedScopeChange: number | null;
  isLoading: boolean;
  isLoadingScore: boolean;
  isLoadingKeyMeasures: boolean;
} => {
  const { t } = useTranslation();
  const { selectedBoardId } = useWorkPeriodStore();

  const scoreDataPayload = usePayload([Measure.HealthScores]);
  const { data: scoreData, query: scoreDataQuery } = useWorkPeriodsMeasureSingular<HealthScoreData>({
    payload: {
      ...scoreDataPayload,
      transformer: Transformer.TimeSeriesMostRecentNotNull,
    },
    options: {
      enabled: !!selectedBoardId,
    },
  });

  const { data: keyMeasuresData, isLoading: isLoadingKeyProcessMeasures } = useKeyProcessMeasuresData();
  const { data: flowByPaceData, isLoading: isLoadingFlowByPace } = useFlowByPaceData();

  // Filter first: `transformForPaceChart` strips `tasks_`/`points_` prefixes into shared fields, so unfiltered points_* overwrites tasks_*.
  const calculatedScopeChange = useMemo(() => {
    if (!flowByPaceData) return null;
    const chartData = transformForPaceChart(filterPaceData(flowByPaceData, Metric.Tasks));
    if (chartData.length === 0) return null;
    const latestDate = chartData[chartData.length - 1].date;
    const scopeMetadata = getScopeMetadata(chartData, latestDate, Metric.Tasks, t);
    return scopeMetadata.percent;
  }, [flowByPaceData, t]);

  const isLoadingScore = scoreDataQuery.isLoading;
  // Roll the flow-by-pace loading state into the key-measures flag because
  // one of the rendered cards (`scope_creep`) reads `calculatedScopeChange`
  // derived from flow-by-pace data. Without this the scope_creep tile would
  // silently snap from `--` to a real percent later than its peers.
  const isLoadingKeyMeasures = isLoadingKeyProcessMeasures || isLoadingFlowByPace;
  const isLoading = isLoadingScore || isLoadingKeyMeasures;

  return {
    scoreData,
    keyMeasuresData,
    calculatedScopeChange,
    isLoading,
    isLoadingScore,
    isLoadingKeyMeasures,
  };
};

/**
 * Custom hook to fetch audit log data for work periods.
 *
 * @returns {object} An object containing the fetched data and loading state.
 * @property {AuditLogResponse | undefined} data - The audit log data fetched from the API.
 * @property {boolean} isFetching - A flag indicating whether the data is currently being fetched.
 */
const useWorkPeriodsAuditLogData = (): {
  data: AuditLogResponse | undefined;
  isFetching: boolean;
} => {
  const { selectedBoardId } = useWorkPeriodStore();

  // Create payload for the audit log measure
  const payload = usePayload([Measure.AuditLog]);

  const { data, query } = useWorkPeriodsMeasureSingular<AuditLogResponse>({
    payload: {
      ...payload,
    },
    options: {
      enabled: !!selectedBoardId,
    },
  });

  return {
    data,
    isFetching: query.isFetching,
  };
};

/**
 * Mapping from API bucket names to Bloomfilter status names.
 * Some backends return simplified bucket names (todo, doing, done) that need to be
 * mapped to the expected Bloomfilter statuses (backlog, wip, done).
 */
const API_BUCKET_TO_STATUS_MAP: Record<string, string> = {
  todo: 'backlog',
  doing: 'wip',
};

/**
 * Normalizes task flow history data by mapping API bucket names to Bloomfilter statuses.
 * For example, 'todo' is mapped to 'backlog' and 'doing' is mapped to 'wip'.
 *
 * @param {TaskFlowHistoryType | undefined} data - The raw task flow history data from the API
 * @returns {TaskFlowHistoryType | undefined} - The normalized task flow history data
 */
const normalizeTaskFlowData = (
  data: (TaskFlowHistoryType & { phases?: PhaseLabel[] }) | undefined,
): TaskFlowHistoryType | undefined => {
  if (!data) return undefined;

  const normalizedData: TaskFlowHistoryType = {};

  for (const dateKey of Object.keys(data)) {
    // Skip the `phases` companion array — it rides on the same
    // response object but is presentation metadata, not a date entry.
    if (dateKey === 'phases') continue;
    const dateData = data[dateKey];
    if (!dateData || typeof dateData !== 'object') continue;
    const normalizedDateData: Record<string, TaskFlowHistoryTask[]> = {};

    for (const bucketKey of Object.keys(dateData)) {
      const normalizedKey = API_BUCKET_TO_STATUS_MAP[bucketKey] || bucketKey;
      const tasks = dateData[bucketKey as keyof TaskFlowHistoryOnDate] ?? [];

      if (normalizedDateData[normalizedKey]) {
        // Merge tasks if the normalized key already exists (e.g., if API returns both 'todo' and 'backlog')
        normalizedDateData[normalizedKey] = [...normalizedDateData[normalizedKey], ...tasks];
      } else {
        normalizedDateData[normalizedKey] = tasks;
      }
    }

    normalizedData[dateKey] = normalizedDateData as TaskFlowHistoryOnDate;
  }

  return normalizedData;
};

/**
 * Custom hook to fetch the task flow data for the selected work periods or
 * date range, surfacing the optional `phases` companion array so column
 * headers can render per-workflow display labels.
 *
 * @returns {object} An object containing the fetched data, the optional
 *   per-workflow phase labels, an `isFetching` flag (true on initial load
 *   and background refetches) and an `isLoading` flag (true only while no
 *   cached data is available yet — used to drive scoped chart overlays
 *   without flashing on cached navigations).
 */
const useWorkPeriodsTaskFlowData = (): {
  data: TaskFlowHistoryType | undefined;
  phases: PhaseLabel[] | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const { selectedBoardId } = useWorkPeriodStore();

  const payload = usePayload([Measure.TaskFlow]);
  const { data: rawData, query } = useWorkPeriodsMeasureSingular<TaskFlowHistoryType & { phases?: PhaseLabel[] }>({
    payload,
    options: {
      enabled: !!selectedBoardId,
    },
  });

  // Normalize API bucket names to Bloomfilter statuses (e.g., 'todo' -> 'backlog', 'doing' -> 'wip')
  const data = normalizeTaskFlowData(rawData);
  const phases = rawData?.phases;

  return { data, phases, isFetching: query.isFetching, isLoading: query.isLoading };
};

/**
 * Custom hook to fetch the tasks data for the selected work periods or date range
 *
 * @param {Measure[]} measures - The measures to fetch
 * @param {Partial<UseQueryOptions<MinifiedTask[]>>} options - The options for the useQuery hook
 * @returns {object} - An object containing the fetched data and an isFetching flag
 */
const useWorkPeriodsTasksData = (
  measures: Measure[] | string[],
  measureFilter?: string,
  options?: Partial<UseQueryOptions<MinifiedTask[]>>,
  overridePayload?: UseWorkPeriodsMeasurePayloadSingular,
): { data: MinifiedTask[] | undefined; isFetching: boolean } => {
  const { selectedBoardId } = useWorkPeriodStore();

  const payload = usePayload(measures as Measure[]);
  if (measureFilter) {
    payload.measure_filter = measureFilter;
  }

  const payloadToSend = overridePayload ? overridePayload : payload;
  payloadToSend.measure_name = measures as Measure[];

  const { data, query } = useWorkPeriodsTasksSingular({
    payload: payloadToSend,
    options: {
      enabled: !!selectedBoardId,
      ...options,
    },
  });

  return { data, isFetching: query.isFetching };
};

export {
  useAssessmentViewData,
  useBurnsData,
  useFlowByPaceData,
  useFlowByPhaseData,
  usePayload,
  useWorkPeriodsAuditLogData,
  useWorkPeriodsTaskFlowData,
  useWorkPeriodsTasksData,
};
